import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Key, Copy, Check, RefreshCw, Trash2, Download, Play, AlertTriangle,
  Shield, Clock, Plug, FileJson, FileSpreadsheet, Loader2, Filter, BarChart2, Save,
  Settings2, Activity,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend,
} from 'recharts';
import SecuritySection from './SecuritySection';
import SandboxSection from './SandboxSection';
import WebhooksSection from './WebhooksSection';
import ApiDocsSection from './ApiDocsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPE_PRESETS = {
  readonly: {
    label: 'Nur lesen',
    scopes: ['materials:read', 'wanted:read', 'orders:read', 'messages:read'],
  },
  materials_manage: {
    label: 'Materialien verwalten',
    scopes: ['materials:read', 'materials:write', 'wanted:read', 'wanted:write'],
  },
  full: {
    label: 'Vollzugriff',
    scopes: [
      'materials:read', 'materials:write',
      'wanted:read', 'wanted:write',
      'orders:read', 'orders:write',
      'messages:read', 'webhooks:manage',
    ],
  },
};

const STATUS_STYLES = {
  active: { label: 'Aktiv', cls: 'bg-emerald-100 text-emerald-700' },
  expiring_soon: { label: 'Läuft bald ab', cls: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Abgelaufen', cls: 'bg-slate-100 text-slate-600' },
  revoked: { label: 'Widerrufen', cls: 'bg-red-100 text-red-700' },
  compromised: { label: 'Kompromittiert', cls: 'bg-red-200 text-red-800' },
};

const daysUntil = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
};

export default function ApiIntegrationTab({ siteId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [secretDialog, setSecretDialog] = useState(null); // { secret, token_doc, mode }
  const [editDialog, setEditDialog] = useState(null); // full token doc being edited
  const [pullJobs, setPullJobs] = useState([]);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const token = localStorage.getItem('token');
      const [tRes, pRes] = await Promise.all([
        axios.get(`${API}/portal/sites/${siteId}/api-tokens`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/portal/sites/${siteId}/pull-jobs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(tRes.data);
      setAvailableScopes(tRes.data.available_scopes || []);
      setPullJobs(pRes.data?.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Schnittstellen-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  // Poll running pull jobs every 3s
  useEffect(() => {
    const hasRunning = pullJobs.some(j => j.status === 'queued' || j.status === 'running');
    if (!hasRunning) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(load, 3000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [pullJobs, load]);

  const handleCreate = async (body) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/portal/sites/${siteId}/api-tokens`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecretDialog({ secret: res.data.secret, token_doc: res.data.token, mode: 'created' });
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Token konnte nicht erstellt werden');
    }
  };

  const handleRotate = async (tokenId) => {
    if (!window.confirm('Token rotieren? Das neue Token wird einmalig angezeigt; das alte bleibt für 72 h gültig.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/portal/sites/${siteId}/api-tokens/${tokenId}/rotate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecretDialog({ secret: res.data.secret, token_doc: res.data.token, mode: 'rotated' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation fehlgeschlagen');
    }
  };

  const handleRevoke = async (tokenId) => {
    const reason = window.prompt('Widerrufs-Grund (optional):') || '';
    if (reason === null) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/portal/sites/${siteId}/api-tokens/${tokenId}/revoke`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Token widerrufen');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Widerruf fehlgeschlagen');
    }
  };

  const handleSaveEdit = async (updates) => {
    if (!editDialog) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/portal/sites/${siteId}/api-tokens/${editDialog.token_id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Token aktualisiert');
      setEditDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update fehlgeschlagen');
    }
  };

  if (loading) return <div className="p-6 text-muted-foreground">Lade Schnittstellen-Daten…</div>;
  if (!data) return <div className="p-6 text-muted-foreground">Keine Daten verfügbar.</div>;

  return (
    <div className="space-y-6" data-testid="api-integration-tab">
      {/* Status Card */}
      <Card data-testid="api-status-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Plug className="h-5 w-5 text-orange-500" /> Schnittstelle {data.site.site_name}</CardTitle>
              <CardDescription>Ein Token je Integration. Gültig bis zu 365 Tage, mit nahtloser Rotation.</CardDescription>
            </div>
            <Badge className={data.site.api_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}>
              {data.site.api_enabled ? 'Aktiviert' : 'Deaktiviert'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Aktive Tokens" value={data.site.active_tokens} icon={Key} />
          <Kpi label="Tier" value={data.site.api_tier || 'standard'} icon={Shield} />
          <Kpi label="Letzter Zugriff" value={data.site.last_activity_at ? new Date(data.site.last_activity_at).toLocaleDateString('de-DE') : '—'} icon={Clock} />
          <Kpi label="Base-URL" value="/api/v1" icon={FileJson} mono />
        </CardContent>
      </Card>

      {/* API-Dokumentation (gated via Group/Site Platform-Admin Toggle) */}
      <ApiDocsSection enabled={data.site.api_docs_enabled !== false} />

      {/* Tokens Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-lg">API-Tokens</CardTitle>
            <CardDescription>Authentifizierung für externe Systeme (Bearer-Token)</CardDescription>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="token-create-btn" className="bg-orange-500 hover:bg-orange-600">
            <Key className="h-4 w-4 mr-2" /> Token erstellen
          </Button>
        </CardHeader>
        <CardContent>
          {data.data.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground" data-testid="tokens-empty">
              Noch keine Tokens. Erstellen Sie das erste Token, um Ihr Kundensystem anzubinden.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4">Label</th>
                    <th className="py-2 pr-4">Prefix</th>
                    <th className="py-2 pr-4">Scopes</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Läuft ab</th>
                    <th className="py-2 pr-4">Zuletzt genutzt</th>
                    <th className="py-2 pr-4">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map(t => {
                    const s = STATUS_STYLES[t.status] || STATUS_STYLES.active;
                    const d = daysUntil(t.expires_at);
                    return (
                      <tr key={t.token_id} className="border-b hover:bg-muted/30" data-testid={`token-row-${t.token_id}`}>
                        <td className="py-3 pr-4 text-xs">{t.label}{t.auto_rotate && (
                          <Badge className="ml-2 bg-orange-50 text-orange-700 text-[10px]" data-testid={`token-auto-rotate-badge-${t.token_id}`}>AUTO-ROTATE</Badge>
                        )}</td>
                        <td className="py-3 pr-4 font-mono text-xs">{t.token_prefix}</td>
                        <td className="py-3 pr-4 text-xs">{(t.scopes || []).join(', ')}</td>
                        <td className="py-3 pr-4"><Badge className={s.cls}>{s.label}</Badge></td>
                        <td className="py-3 pr-4 text-xs">
                          {t.expires_at ? new Date(t.expires_at).toLocaleDateString('de-DE') : '—'}
                          {d !== null && d >= 0 && t.status !== 'revoked' && t.status !== 'expired' && (
                            <span className={`ml-2 ${d <= 14 ? 'text-amber-600' : 'text-muted-foreground'}`}>({d} T)</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-xs">{t.last_used_at ? new Date(t.last_used_at).toLocaleString('de-DE') : 'nie'}</td>
                        <td className="py-3 pr-4">
                          <div className="flex gap-1">
                            {['active', 'expiring_soon'].includes(t.status) && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => setEditDialog(t)} data-testid={`token-edit-btn-${t.token_id}`} title="Scopes / Auto-Rotate bearbeiten">
                                  <Settings2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleRotate(t.token_id)} data-testid={`token-rotate-btn-${t.token_id}`}>
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRevoke(t.token_id)} data-testid={`token-revoke-btn-${t.token_id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pull Jobs / Daten-Sync */}
      <PullJobsSection siteId={siteId} jobs={pullJobs} reload={load} />

      {/* Phase 4 – Paket A: Security & Access Control */}
      <SecuritySection siteId={siteId} availableScopes={availableScopes} />

      {/* Phase 4 – Paket B: Sandbox & API Docs */}
      <SandboxSection />

      {/* Phase 4 – Paket C: Webhooks */}
      <WebhooksSection siteId={siteId} />

      {/* Usage Cockpit */}
      <UsageCockpit siteId={siteId} />

      {/* Import Rules + Age Histogram */}
      <ImportRulesSection siteId={siteId} />

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        availableScopes={availableScopes}
      />

      <EditTokenDialog
        entry={editDialog}
        onClose={() => setEditDialog(null)}
        onSave={handleSaveEdit}
        availableScopes={availableScopes}
      />

      <SecretDialog
        entry={secretDialog}
        onClose={() => setSecretDialog(null)}
      />
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────

const Kpi = ({ label, value, icon: Icon, mono = false }) => (
  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
    <div className="p-2 bg-orange-50 rounded-md"><Icon className="h-4 w-4 text-orange-600" /></div>
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-sm font-semibold ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  </div>
);

// ─── Create Token Dialog ──────────────────────────────────────────

function CreateTokenDialog({ open, onOpenChange, onSubmit, availableScopes }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState(SCOPE_PRESETS.readonly.scopes);
  const [lifetime, setLifetime] = useState('180');
  const [preset, setPreset] = useState('readonly');
  const [autoRotate, setAutoRotate] = useState(false);

  useEffect(() => {
    if (open) { setLabel(''); setScopes(SCOPE_PRESETS.readonly.scopes); setLifetime('180'); setPreset('readonly'); setAutoRotate(false); }
  }, [open]);

  const toggleScope = (s) => {
    setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    setPreset('custom');
  };

  const applyPreset = (key) => {
    setPreset(key);
    if (SCOPE_PRESETS[key]) setScopes(SCOPE_PRESETS[key].scopes);
  };

  const submit = () => {
    if (!label.trim()) { toast.error('Bitte ein Label vergeben'); return; }
    if (scopes.length === 0) { toast.error('Mindestens ein Scope wählen'); return; }
    onSubmit({ label: label.trim(), scopes, lifetime_days: parseInt(lifetime, 10), auto_rotate: autoRotate });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="token-create-dialog">
        <DialogHeader>
          <DialogTitle>Neues API-Token erstellen</DialogTitle>
          <DialogDescription>
            Das Token wird nach Erstellung einmalig angezeigt — bitte unbedingt sicher speichern.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="z.B. SAP Produktivsystem" data-testid="token-label-input" />
          </div>
          <div>
            <Label>Preset</Label>
            <div className="flex gap-2 flex-wrap mt-1">
              {Object.entries(SCOPE_PRESETS).map(([k, v]) => (
                <Button
                  key={k}
                  type="button"
                  variant={preset === k ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyPreset(k)}
                  data-testid={`scope-preset-${k}`}
                >
                  {v.label}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="grid grid-cols-2 gap-2 mt-1 border rounded-md p-3">
              {availableScopes.map(s => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={scopes.includes(s)} onCheckedChange={() => toggleScope(s)} data-testid={`scope-${s}`} />
                  <span className="font-mono text-xs">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Gültigkeit</Label>
            <Select value={lifetime} onValueChange={setLifetime}>
              <SelectTrigger data-testid="token-lifetime-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 Tage</SelectItem>
                <SelectItem value="90">90 Tage</SelectItem>
                <SelectItem value="180">180 Tage (empfohlen)</SelectItem>
                <SelectItem value="365">365 Tage</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer border rounded-md p-3 bg-orange-50/40">
            <Checkbox
              checked={autoRotate}
              onCheckedChange={(v) => setAutoRotate(!!v)}
              data-testid="token-auto-rotate"
              className="mt-0.5"
            />
            <div>
              <p className="font-medium">Automatische Rotation</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                7 Tage vor Ablauf wird das Token automatisch erneuert. Das neue Token wird per E-Mail an
                die Site-Admins zugestellt. Vorgänger bleibt 72 h gültig (null Downtime).
              </p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} className="bg-orange-500 hover:bg-orange-600" data-testid="token-create-submit">Token erstellen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Secret Reveal Dialog ─────────────────────────────────────────

function SecretDialog({ entry, onClose }) {
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);
  if (!entry) return null;
  const copy = async () => {
    await navigator.clipboard.writeText(entry.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { if (ack) onClose(); else toast.error('Bitte bestätigen Sie, dass Sie das Token gespeichert haben.'); } }}>
      <DialogContent className="max-w-lg" data-testid="token-secret-modal">
        <DialogHeader>
          <DialogTitle>
            {entry.mode === 'rotated' ? 'Token rotiert — neues Geheimnis' : 'Token erstellt — Geheimnis einmalig'}
          </DialogTitle>
          <DialogDescription>
            Dieses vollständige Token wird <strong>nicht erneut angezeigt</strong>. Bitte jetzt kopieren und sicher ablegen.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            Behandeln Sie dieses Token wie ein Passwort. Es gewährt den Scopes {entry.token_doc?.scopes?.join(', ')} Zugriff auf Ihren Standort.
          </p>
        </div>
        <div className="bg-slate-950 text-slate-100 p-3 rounded-lg font-mono text-xs break-all" data-testid="token-secret-value">
          {entry.secret}
        </div>
        <Button onClick={copy} variant="outline" className="w-full" data-testid="token-copy-btn">
          {copied ? <><Check className="h-4 w-4 mr-2 text-emerald-600" /> Kopiert</> : <><Copy className="h-4 w-4 mr-2" /> In Zwischenablage kopieren</>}
        </Button>
        {entry.mode === 'rotated' && (
          <p className="text-xs text-muted-foreground">
            Hinweis: Das vorherige Token bleibt noch 72 Stunden gültig, damit Sie Ihre Integration ohne Ausfall umstellen können.
          </p>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={ack} onCheckedChange={setAck} data-testid="token-secret-ack" />
          <span>Ich habe das Token sicher gespeichert.</span>
        </label>
        <DialogFooter>
          <Button onClick={onClose} disabled={!ack} className="bg-orange-500 hover:bg-orange-600" data-testid="token-secret-close">Fertig</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Token Dialog ───────────────────────────────────────────

function EditTokenDialog({ entry, onClose, onSave, availableScopes }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState([]);
  const [autoRotate, setAutoRotate] = useState(false);

  useEffect(() => {
    if (entry) {
      setLabel(entry.label || '');
      setScopes(entry.scopes || []);
      setAutoRotate(!!entry.auto_rotate);
    }
  }, [entry]);

  if (!entry) return null;

  const toggleScope = (s) => setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const dirty =
    label !== (entry.label || '') ||
    JSON.stringify([...scopes].sort()) !== JSON.stringify([...(entry.scopes || [])].sort()) ||
    autoRotate !== !!entry.auto_rotate;

  const submit = () => {
    if (scopes.length === 0) { toast.error('Mindestens ein Scope wählen'); return; }
    const updates = {};
    if (label !== (entry.label || '')) updates.label = label.trim();
    if (JSON.stringify([...scopes].sort()) !== JSON.stringify([...(entry.scopes || [])].sort())) updates.scopes = scopes;
    if (autoRotate !== !!entry.auto_rotate) updates.auto_rotate = autoRotate;
    onSave(updates);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="token-edit-dialog">
        <DialogHeader>
          <DialogTitle>Token bearbeiten</DialogTitle>
          <DialogDescription>Prefix: <span className="font-mono text-xs">{entry.token_prefix}</span></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} data-testid="token-edit-label" />
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="grid grid-cols-2 gap-2 mt-1 border rounded-md p-3">
              {availableScopes.map(s => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={scopes.includes(s)} onCheckedChange={() => toggleScope(s)} data-testid={`token-edit-scope-${s}`} />
                  <span className="font-mono text-xs">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer border rounded-md p-3 bg-orange-50/40">
            <Checkbox checked={autoRotate} onCheckedChange={(v) => setAutoRotate(!!v)} data-testid="token-edit-auto-rotate" className="mt-0.5" />
            <div>
              <p className="font-medium">Automatische Rotation</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                7 Tage vor Ablauf automatisch erneuern. Neues Token kommt per E-Mail.
              </p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={submit}
            disabled={!dirty}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="token-edit-save"
          >Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Usage Cockpit ───────────────────────────────────────────────

function UsageCockpit({ siteId }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${API}/portal/sites/${siteId}/api-usage?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [siteId, days]);

  return (
    <Card data-testid="api-usage-cockpit">
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2"><Activity className="h-5 w-5 text-orange-500" /> Nutzungs-Cockpit</CardTitle>
          <CardDescription>API-Calls der letzten {days} Tage · Top-Endpoints · Fehlerquote</CardDescription>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="usage-cockpit-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 Tage</SelectItem>
            <SelectItem value="30">30 Tage</SelectItem>
            <SelectItem value="90">90 Tage</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Lade Nutzungsdaten…</div>
        ) : !data ? (
          <div className="text-sm text-muted-foreground">Keine Daten verfügbar.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-muted/30 rounded-md" data-testid="usage-total">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Requests</p>
                <p className="text-xl font-semibold tabular-nums">{(data.total_requests || 0).toLocaleString('de-DE')}</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-md" data-testid="usage-error-rate">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Fehlerquote</p>
                <p className={`text-xl font-semibold tabular-nums ${data.error_rate > 5 ? 'text-red-600' : ''}`}>{data.error_rate}%</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-md" data-testid="usage-endpoints-count">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Endpoints genutzt</p>
                <p className="text-xl font-semibold tabular-nums">{data.top_endpoints?.length || 0}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-56" data-testid="usage-daily-chart">
                <p className="text-xs text-muted-foreground mb-2">Requests pro Tag</p>
                {(data.daily || []).length === 0 ? (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded-md">Noch keine API-Calls</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="requests" stroke="#F97316" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="errors" stroke="#DC2626" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="h-56" data-testid="usage-top-endpoints-chart">
                <p className="text-xs text-muted-foreground mb-2">Top Endpoints</p>
                {(data.top_endpoints || []).length === 0 ? (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded-md">Keine Daten</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.top_endpoints.slice(0, 8)} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="path" tick={{ fontSize: 10 }} width={160} />
                      <Tooltip />
                      <Bar dataKey="requests" fill="#F97316" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Pull Jobs ───────────────────────────────────────────────────

function PullJobsSection({ siteId, jobs, reload }) {
  const [submitting, setSubmitting] = useState(false);
  const [resources, setResources] = useState({ materials: true, wanted: true, orders: false, matches: false });
  const [fmt, setFmt] = useState('jsonl');
  const [since, setSince] = useState('');

  const trigger = async () => {
    const picked = Object.entries(resources).filter(([, v]) => v).map(([k]) => k);
    if (picked.length === 0) { toast.error('Bitte mindestens eine Ressource wählen'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/portal/sites/${siteId}/pull-jobs`,
        { resources: picked, format: fmt, updated_since: since || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Export-Job gestartet');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Job konnte nicht gestartet werden');
    } finally {
      setSubmitting(false);
      setTimeout(() => setSubmitting(false), 2000);
    }
  };

  const download = (job) => {
    if (!job.result_url) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}${job.result_url}`;
    window.open(url, '_blank');
  };

  return (
    <Card data-testid="pull-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><Download className="h-5 w-5 text-orange-500" /> Daten-Abruf (manueller Export)</CardTitle>
        <CardDescription>Auf Knopfdruck Ihre Standort-Daten als JSONL, JSON oder CSV exportieren</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Ressourcen</Label>
            <div className="space-y-2 mt-1">
              {[
                ['materials', 'Materialien'],
                ['wanted', 'Bedarfe'],
                ['orders', 'Bestellungen'],
                ['matches', 'Matches'],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={resources[k]}
                    onCheckedChange={(v) => setResources(r => ({ ...r, [k]: !!v }))}
                    data-testid={`pull-resource-${k}`}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Geändert seit (optional)</Label>
            <Input type="date" value={since} onChange={e => setSince(e.target.value ? `${e.target.value}T00:00:00Z` : '')} data-testid="pull-since-date" className="mt-1" />
            <Label className="mt-3 block">Format</Label>
            <Select value={fmt} onValueChange={setFmt}>
              <SelectTrigger data-testid="pull-format-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="jsonl">JSONL (eine Zeile pro Datensatz)</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="csv">CSV (Excel)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col justify-end gap-2">
            <Button
              onClick={trigger}
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600 h-12 text-base"
              data-testid="pull-trigger-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Jetzt abrufen
            </Button>
            <p className="text-[11px] text-muted-foreground">max. 10 Jobs / Stunde / Standort</p>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Letzte Jobs</Label>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Exporte.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {jobs.map(j => (
                <div key={j.job_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg text-sm" data-testid={`pull-job-row-${j.job_id}`}>
                  <div className="flex items-center gap-3">
                    {j.status === 'completed' && <Check className="h-4 w-4 text-emerald-600" />}
                    {j.status === 'failed' && <AlertTriangle className="h-4 w-4 text-red-600" />}
                    {(j.status === 'queued' || j.status === 'running') && <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />}
                    <div>
                      <p className="font-medium">{(j.resources || []).join(', ')} · {j.format?.toUpperCase()}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(j.requested_at).toLocaleString('de-DE')}
                        {j.counts && ' · ' + Object.entries(j.counts).map(([k, v]) => `${k}:${v}`).join(' ')}
                        {j.error && ` · ${j.error}`}
                      </p>
                    </div>
                  </div>
                  {j.status === 'completed' && j.result_url && (
                    <Button size="sm" variant="outline" onClick={() => download(j)} data-testid={`pull-job-download-btn-${j.job_id}`}>
                      {j.format === 'csv' ? <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> : <FileJson className="h-3.5 w-3.5 mr-1" />}
                      Download
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Import Rules + Age Histogram ──────────────────────────────────

const BUCKET_LABELS = {
  '0_90': 'Frisch (0–90 T)',
  '91_180': 'Mittel (91–180 T)',
  '181_360': 'Alt (181–360 T)',
  'gt_360': 'Sehr alt (>360 T)',
  'unknown': 'Ohne Datum',
};
const BUCKET_COLORS = {
  '0_90': 'bg-emerald-500',
  '91_180': 'bg-lime-500',
  '181_360': 'bg-amber-500',
  'gt_360': 'bg-red-500',
  'unknown': 'bg-slate-400',
};

function ImportRulesSection({ siteId }) {
  const [rules, setRules] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reindexing, setReindexing] = useState(false);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const token = localStorage.getItem('token');
      const [r, s] = await Promise.all([
        axios.get(`${API}/portal/sites/${siteId}/import-rules`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/portal/sites/${siteId}/import-stats?days=30`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setRules(r.data);
      setStats(s.data);
    } catch {
      // silent
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/sites/${siteId}/import-rules`, {
        enabled: rules.enabled,
        max_age_days: rules.max_age_days === '' ? null : (rules.max_age_days ?? null),
        min_quantity: rules.min_quantity === '' ? null : (rules.min_quantity ?? null),
        allowed_category_codes: rules.allowed_category_codes || [],
        denied_category_codes: rules.denied_category_codes || [],
        require_fields: rules.require_fields || [],
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Import-Regeln gespeichert');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const reindex = async () => {
    setReindexing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/portal/sites/${siteId}/reindex-age`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.updated} Materialien neu berechnet`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Neu-Berechnung fehlgeschlagen');
    } finally {
      setReindexing(false);
    }
  };

  if (!rules) return null;
  const live = stats?.live_inventory_histogram || {};
  const liveTotal = stats?.live_inventory_total || 0;
  const ingest = stats?.ingest_histogram || {};
  const ingestTotal = Object.values(ingest).reduce((a, b) => a + b, 0);

  return (
    <Card data-testid="import-rules-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><Filter className="h-5 w-5 text-orange-500" /> Import-Filter &amp; Altersanalyse</CardTitle>
        <CardDescription>Regeln für automatische Daten-Akzeptanz pro Standort · Altersverteilung wird live berechnet</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Live Age Histogram */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="flex items-center gap-2"><BarChart2 className="h-4 w-4" /> Live-Bestand nach Alter ({liveTotal} SKUs)</Label>
            <Button size="sm" variant="outline" onClick={reindex} disabled={reindexing} data-testid="reindex-age-btn">
              {reindexing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Alter neu berechnen
            </Button>
          </div>
          <AgeBars data={live} total={liveTotal} testid="live-histogram" />
        </div>

        {/* Ingest stats (30d) */}
        <div>
          <Label className="mb-2 block">Import-Aktivität (30 Tage): {stats?.accepted || 0} akzeptiert · {stats?.rejected || 0} abgelehnt</Label>
          <AgeBars data={ingest} total={ingestTotal} testid="ingest-histogram" />
          {(stats?.top_reasons || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {stats.top_reasons.map(r => (
                <Badge key={r.reason} variant="outline" className="text-xs" data-testid={`reason-${r.reason}`}>
                  {r.reason}: {r.count}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Rules Editor */}
        <div className="border-t pt-4 space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!rules.enabled}
              onCheckedChange={v => setRules({ ...rules, enabled: !!v })}
              data-testid="rules-enabled"
            />
            <Label className="cursor-pointer">Import-Filter aktiv</Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Max. Alter bei Import (Tage)</Label>
              <Input
                type="number"
                min="0"
                placeholder="z.B. 180 (leer = kein Limit)"
                value={rules.max_age_days ?? ''}
                onChange={e => setRules({ ...rules, max_age_days: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                data-testid="rule-max-age"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Ältere Bestände werden bei Import abgelehnt (422).</p>
            </div>
            <div>
              <Label>Min. Menge</Label>
              <Input
                type="number"
                min="0"
                step="any"
                placeholder="z.B. 50 (leer = keine Mindestmenge)"
                value={rules.min_quantity ?? ''}
                onChange={e => setRules({ ...rules, min_quantity: e.target.value === '' ? null : parseFloat(e.target.value) })}
                data-testid="rule-min-qty"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Kleinst-Mengen werden gefiltert.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Erlaubte Kategorie-Codes (kommasepariert)</Label>
              <Input
                placeholder="leer = alle"
                value={(rules.allowed_category_codes || []).join(',')}
                onChange={e => setRules({ ...rules, allowed_category_codes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                data-testid="rule-allowed"
              />
            </div>
            <div>
              <Label>Verbotene Kategorie-Codes (kommasepariert)</Label>
              <Input
                placeholder="leer = keine Sperre"
                value={(rules.denied_category_codes || []).join(',')}
                onChange={e => setRules({ ...rules, denied_category_codes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                data-testid="rule-denied"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="rules-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Regeln speichern
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AgeBars({ data, total, testid }) {
  const order = ['0_90', '91_180', '181_360', 'gt_360', 'unknown'];
  return (
    <div className="space-y-2" data-testid={testid}>
      {order.map(k => {
        const v = data[k] || 0;
        const pct = total > 0 ? Math.round((v / total) * 100) : 0;
        return (
          <div key={k} className="flex items-center gap-3" data-testid={`${testid}-${k}`}>
            <span className="text-xs w-40 flex-shrink-0">{BUCKET_LABELS[k]}</span>
            <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
              <div className={`${BUCKET_COLORS[k]} h-full transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-mono w-20 text-right text-muted-foreground">{v} ({pct}%)</span>
          </div>
        );
      })}
    </div>
  );
}

